import React, { useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Helmet } from 'react-helmet-async'

import Page from 'components/Page'
import { mailto, SUPPORT_EMAILS } from 'utils/supportEmails'

const consumerQuestions = [
  {
    question: 'What happened to the offer I found earlier?',
    answer: 'When a business provides an offer through HiddenUnderGround, they have the option to display that offer for a set amount of time, or until the offer has been claimed a set number of times. Offers display their expiration and the remaining number of claims. If you have a HiddenUnderGround account and favorited or claimed the offer, you can check whether it has expired.',
  },
  {
    question: 'Will an offer that has expired, or is no longer searchable, return?',
    answer: 'It is up to the business to determine if they want to rerun an offer.',
  },
  {
    question: 'Do I need to create an account to see the offer code?',
    answer: 'No, you do not need to create an account, but we recommend creating one to save your favorite and claimed offers.',
  },
  {
    question: 'Why should I create a personal account?',
    answer: 'You do not need to create a personal account to use the site. Personal accounts are provided as a convenient way to save offers you like (favorited), save a copy of an offer you claimed, set alerts, and more accurately find local offers based on your location.',
  },
  {
    question: 'You say offers should work 99% of the time, but my offer code didn’t work for a specific item. Why?',
    answer: 'Certain manufacturers will not allow any offers, discounts, or special pricing to be applied to their products. It is important to check the offer details to make sure the item you want to buy is included in the offer.',
  },
  {
    question: 'I claimed an offer and wrote down my code. A few days later, I tried to use my code, but it didn’t work. Why?',
    answer: 'When you claimed your offer, the offer code was valid. Offer codes have expiration dates, and it is possible that the code expired before you were able to use it. If you created a personal account with HiddenUnderGround, check whether the claimed offer is now listed as unavailable.',
  },
  {
    question: 'I read the offer’s terms and conditions. The item I want to buy is not excluded, but my offer code still did not work. What should I do?',
    answer: (
      <>
        99% offer accuracy is important to us. If you have a HiddenUnderGround account, you can flag the offer in your claimed menu. If you do not have a HiddenUnderGround account and believe that a claimed offer should have worked but didn’t, send us an email <a href={mailto(SUPPORT_EMAILS.badOffer)}>here</a>. If we receive multiple complaints against an offer, we will remove it for review.
      </>
    ),
  },
  {
    question: 'I found a better offer on HiddenUnderGround. Can I cancel my order?',
    answer: 'HiddenUnderGround does not sell the products or services associated with the offer. If you wish to cancel an order or ask for a price adjustment, you will need to contact the company from which the purchase was made.',
  },
  {
    question: 'The second time I tried to use the offer code it didn’t work. Why?',
    answer: 'Some manufacturers and retailers only provide a one-time-use offer. It is important to check the offer details for any limitations.',
  },
  {
    question: 'Where do I make my purchase?',
    answer: 'HiddenUnderGround does not sell any products or services through the site. If the offer is an online code, you can click the offer website icon to be taken to the customer’s site. In their shopping cart, add the code for the discount. If the offer is a bar code or QR code, you will need to visit their store to redeem the offer.',
  },
]

const businessQuestions = [
  {
    question: 'Who is Kaiylee?',
    answer: 'Kaiylee is HiddenUnderGround’s AI Marketing Assistant. She is here to help you create and manage your offers.',
  },
  {
    question: 'Should I provide a generic offer code, or one specific to HiddenUnderGround?',
    answer: 'When creating an offer code, it is recommended that you create a HiddenUnderGround-specific code. This will allow you to track and measure the effectiveness of your HiddenUnderGround campaign.',
  },
  {
    question: 'Can I use a pre-generated offer code?',
    answer: 'You can use any valid pre-generated offer code, bar code, or QR code. We recommend using your analytics to measure the effectiveness of your HiddenUnderGround campaign.',
  },
  {
    question: 'How long should I run my offer on HUG?',
    answer: 'You are in control of the offer duration and should set the length based on your campaign.',
  },
  {
    question: 'My offer is a huge success. How do I extend the offer?',
    answer: 'Within your account, under offers, click current offers. Find the offer you want to extend and click “clone.” This will place your offer in the draft folder for editing. You can also use Kaiylee, our AI Marketing Assistant, to help adjust your offer.',
  },
  {
    question: 'Can I schedule an offer go-live date?',
    answer: 'Yes, you have the ability to schedule your offer’s start date in the future.',
  },
  {
    question: 'Can I stop or pause an offer before it expires?',
    answer: 'Yes, you can pause an offer whenever you like. Note that registered users who claim your offer will have a copy of that offer in their account. We recommend that you set your terms and conditions to allow for this.',
  },
  {
    question: 'How many offers can I run at a time?',
    answer: 'You can run as many offers as you like, but we suggest that they are different.',
  },
  {
    question: 'My offers are performing really well, but it looks like they have all paused. What happened?',
    answer: (
      <>
        This can occur for a few reasons. First, check your email to see if you have received any notifications. Second, check your payments dashboard to make sure your payment method is up to date and auto-reload is on. You may have insufficient funds. Next, check whether your offer has been flagged by our users for review. This can occur if too many users believe your offer is not valid. If none of the above have occurred, contact us <a href={mailto(SUPPORT_EMAILS.help)}>here</a>.
      </>
    ),
  },
  {
    question: 'Are my sales processed through your site?',
    answer: 'No, HiddenUnderGround does not process any purchases on its site. We are an offer platform designed to help consumers find great offers and discounts.',
  },
  {
    question: 'Is HiddenUnderGround an affiliate site?',
    answer: 'No, HiddenUnderGround will not create an affiliate agreement.',
  },
  {
    question: 'Does HiddenUnderGround take a percentage of my sales?',
    answer: 'No, HiddenUnderGround will not take a percentage of sales generated from the HiddenUnderGround site.',
  },
  {
    question: 'How does HiddenUnderGround charge?',
    answer: 'HiddenUnderGround charges a CPC (cost per claim) only when a visitor claims your offer.',
  },
  {
    question: 'Will HiddenUnderGround tell me what offer I have to run?',
    answer: 'No, HiddenUnderGround believes you should be in charge of the offers you want to run. But if you are unsure which offer is best, just ask Kaiylee for help.',
  },
]

const FAQSection = ({ title, questions }) => (
  <Box my={6}>
    <Typography variant="h5" component="h2" gutterBottom>
      {title}
    </Typography>
    {questions.map(({ question, answer }) => (
      <Accordion key={question}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{question}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="textSecondary" paragraph>
            {answer}
          </Typography>
        </AccordionDetails>
      </Accordion>
    ))}
  </Box>
)

const FAQ = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <Page>
      <Helmet>
        <title>Help - HiddenUnderGround</title>
      </Helmet>
      <Container maxWidth="md">
        <Box my={10}>
          <Typography variant="h4" component="h1">
            Frequently asked questions
          </Typography>
          <FAQSection title="Consumers" questions={consumerQuestions} />
          <FAQSection title="Businesses" questions={businessQuestions} />
          <Typography paragraph>
            For additional questions, please email support at <a href={mailto(SUPPORT_EMAILS.help)}>{SUPPORT_EMAILS.help}</a> or visit the <NavLink to="/feedback">feedback page</NavLink>.
          </Typography>
        </Box>
      </Container>
    </Page>
  )
}

export default FAQ
